import moment from 'moment-timezone';
import { BillTypeEnum, ExpenseBillTypeEnum } from '../Components/Expense/BillType.enum';
import { ExpenseClassEnum, ExpenseClassIdEnum } from '../Components/Expense/ExpenseClass.enum';
import { ExpenseStatusValuesEnum } from '../Components/Expense/ExpenseStatus.enum';

// IANA zone for US Eastern Time (ET). There is no "ET" zone id; America/New_York
// is the canonical one and switches between EST and EDT with DST.
export const PAYROLL_TIMEZONE = 'America/New_York';
export const PAYROLL_DAY_OF_WEEK = 5; // Friday, 12:00 AM ET (holiday weeks stay Friday-only)
export const PAY_DATE_FIELD_LABEL = 'Pay Date';
export const PAYROLL_DATE_FIELD_LABEL = 'Payroll Date';

const REIMBURSEMENT_CLASSES = [ExpenseClassEnum.Reimbursement, ExpenseClassIdEnum.Reimbursement];
const PAYMENT_CLASSES = [ExpenseClassEnum.Payment, ExpenseClassIdEnum.Payment];
const CREDIT_CARD_CLASSES = [
    ExpenseClassEnum.CreditCard,
    ExpenseClassEnum.IngestedCCTransaction,
    ExpenseClassIdEnum.IngestedCCTransaction,
    ExpenseClassIdEnum.Normal,
];
const CLINICIAN_DEDUCTION_VALUES = [
    BillTypeEnum.ClinicianDeduction,
    ExpenseBillTypeEnum[3],
    3,
    '3',
];

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const matchesAny = (value, candidates) =>
    candidates.some((candidate) => value === candidate || String(value) === String(candidate));

const toDateKey = (value) => {
    if (value == null || value === '') return null;
    if (typeof value === 'string') {
        const datePart = value.substring(0, 10);
        return DATE_ONLY.test(datePart) ? datePart : null;
    }
    const parsed = moment.isMoment(value) ? value.clone() : moment(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

export const isPayrollDay = (now = moment()) =>
    moment.tz(now, PAYROLL_TIMEZONE).day() === PAYROLL_DAY_OF_WEEK;

export const getExpensePayOrPayrollDate = (expense) => {
    if (matchesAny(expense?.expenseClass, PAYMENT_CLASSES)) {
        return expense?.payrollDate || expense?.payDate || null;
    }
    return expense?.payDate || expense?.payrollDate || null;
};

export const isPayDateSameAsPayrollDay = (expense, now = moment()) => {
    const payDateKey = toDateKey(getExpensePayOrPayrollDate(expense));
    if (!payDateKey) return false;
    const todayEtKey = moment.tz(now, PAYROLL_TIMEZONE).format('YYYY-MM-DD');
    return payDateKey === todayEtKey;
};

export const isClinicianDeductionBillType = (billType) =>
    matchesAny(billType, CLINICIAN_DEDUCTION_VALUES);

export const isPayrollDayWarningEligibleExpense = (expense) => {
    if (!expense) return false;

    if (matchesAny(expense.expenseClass, REIMBURSEMENT_CLASSES)) return true;
    if (matchesAny(expense.expenseClass, PAYMENT_CLASSES)) return true;

    return (
        matchesAny(expense.expenseClass, CREDIT_CARD_CLASSES) &&
        isClinicianDeductionBillType(expense.billType)
    );
};

export const isMovingToApproved = (originalStatus, newStatus) =>
    newStatus === ExpenseStatusValuesEnum.Approved &&
    originalStatus !== ExpenseStatusValuesEnum.Approved;

export const getPayDateFieldLabel = (expense) => {
    if (matchesAny(expense?.expenseClass, PAYMENT_CLASSES)) {
        return PAYROLL_DATE_FIELD_LABEL;
    }
    return PAY_DATE_FIELD_LABEL;
};

export const getPayrollDayWarningMessage = (expense) => {
    const fieldLabel = getPayDateFieldLabel(expense);
    return `Payroll is being processed today, and this expense is dated for today's payroll. It may miss the cutoff. Update the ${fieldLabel} to the next payroll date, or confirm to keep the current ${fieldLabel}.`;
};

export const getPayrollDayWarningExpense = (
    expenses = [],
    originalExpenses = [],
    isCreditMemoFlow = false,
    now = moment()
) =>
    expenses.find((expense, index) => {
        if (!isPayrollDayWarningEligibleExpense(expense)) return false;
        if (!isPayDateSameAsPayrollDay(expense, now)) return false;
        if (isCreditMemoFlow) return true;

        const original =
            originalExpenses.find((entry) => entry?.id && entry.id === expense?.id) ??
            originalExpenses[index];
        return isMovingToApproved(original?.status, expense?.status);
    });

export const shouldShowPayrollDayApprovalWarning = ({
    expenses = [],
    originalExpenses = [],
    isCreditMemoFlow = false,
    now,
} = {}) => {
    if (!isPayrollDay(now)) return false;
    return Boolean(getPayrollDayWarningExpense(expenses, originalExpenses, isCreditMemoFlow, now));
};
